class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    if (this.querySelector('#FacetFiltersFormMobile')) {
      FacetFiltersForm.openMobileFacetGroups(this);

      const mobileDrawerSummary = this.closest('menu-drawer')?.querySelector('.mobile-facets__open-wrapper');
      mobileDrawerSummary?.addEventListener('click', () => {
        window.setTimeout(() => FacetFiltersForm.openMobileFacetGroups(this), 0);
      });
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static bindMobileFacetAccordions(root = document) {
    const mobileFacetForm = root.querySelector?.('#FacetFiltersFormMobile') || document.getElementById('FacetFiltersFormMobile');

    if (!mobileFacetForm || mobileFacetForm.dataset.nazvaAccordionBound === 'true') return;

    mobileFacetForm.dataset.nazvaAccordionBound = 'true';
    mobileFacetForm.addEventListener(
      'click',
      (event) => {
        const summary = event.target.closest('.mobile-facets__details.js-filter > summary');

        if (!summary || !mobileFacetForm.contains(summary)) return;

        event.preventDefault();
        event.stopPropagation();

        const details = summary.parentElement;
        const shouldOpen = !details.hasAttribute('open');

        details.toggleAttribute('open', shouldOpen);
        details.classList.remove('menu-opening');
        summary.setAttribute('aria-expanded', shouldOpen.toString());
        details.closest('.has-submenu')?.classList.remove('submenu-open');
      },
      true
    );
  }

  static openMobileFacetGroups(root = document) {
    FacetFiltersForm.bindMobileFacetAccordions(root);

    root.querySelectorAll('#FacetFiltersFormMobile .mobile-facets__details.js-filter').forEach((details) => {
      if (details.id && details.id.startsWith('Details-Mobile-SortBy-')) return;

      details.setAttribute('open', '');
      details.classList.remove('menu-opening');
      details.querySelector('summary')?.setAttribute('aria-expanded', true);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
    document.querySelectorAll('price-range').forEach((range) => {
      if (typeof range.updateApplyCount === 'function') range.updateApplyCount();
    });
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }

    FacetFiltersForm.openMobileFacetGroups();
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElements = html.querySelectorAll(selector);
      if (!activeFacetsElements.length) return;

      document.querySelectorAll(selector).forEach((element, index) => {
        const activeFacetsElement = activeFacetsElements[index];
        if (!activeFacetsElement) return;

        element.innerHTML = activeFacetsElement.innerHTML;
      });
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const elementSelectors = ['.mobile-facets__open', '.mobile-facets__count'];

    elementSelectors.forEach((selector) => {
      if (!html.querySelector(selector) || !document.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    ['FacetSortForm', 'FacetSortDrawerForm', 'FacetSortMobileForm'].forEach((formId) => {
      const sourceForm = html.getElementById(formId);
      const targetForm = document.getElementById(formId);

      if (sourceForm && targetForm) targetForm.innerHTML = sourceForm.innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer')?.bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const forms = [this.createSearchParams(event.target.closest('form'))];
      const mobileSortForm = document.getElementById('FacetSortMobileForm');

      if (mobileSortForm) forms.push(this.createSearchParams(mobileSortForm));

      this.onSubmitForm(forms.join('&'), event);
    } else {
      const forms = [];
      const sourceFormId = event.target.closest('form').id;
      const isMobile = sourceFormId === 'FacetFiltersFormMobile' || sourceFormId === 'FacetSortMobileForm';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile' || form.id === 'FacetSortMobileForm') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.minInput = this.querySelector('[data-price-min-field]') || this.querySelectorAll('input')[0];
    this.maxInput = this.querySelector('[data-price-max-field]') || this.querySelectorAll('input')[1];
    this.minRange = this.querySelector('[data-price-min-range]');
    this.maxRange = this.querySelector('[data-price-max-range]');
    this.ui = this.querySelector('[data-price-range-ui]');
    this.minOutput = this.querySelector('[data-price-min-output]');
    this.maxOutput = this.querySelector('[data-price-max-output]');
    this.applyButton = this.querySelector('[data-price-range-apply]');
    this.applyCount = this.querySelector('[data-price-range-count]');
    this.rangeMax = this.parsePrice(this.ui?.dataset.priceRangeMax || this.maxInput?.dataset.max || this.maxRange?.max || 0);
    this.currencySymbol = this.ui?.dataset.priceRangeCurrency || '';

    [this.minInput, this.maxInput].filter(Boolean).forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });

    [this.minRange, this.maxRange].filter(Boolean).forEach((element) => {
      element.addEventListener('input', this.onSliderInput.bind(this));
      element.addEventListener('change', this.onSliderCommit.bind(this));
      element.addEventListener('pointerup', this.onSliderCommit.bind(this));
      element.addEventListener('keyup', this.onSliderCommit.bind(this));
    });

    this.applyButton?.addEventListener('click', this.onApplyButtonClick.bind(this));

    this.setMinAndMaxValues();
    this.syncSliderFromFields();
    this.updateApplyCount();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.syncSliderFromFields();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const minInput = this.minInput;
    const maxInput = this.maxInput;
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = this.parsePrice(input.value);
    const min = this.parsePrice(input.getAttribute('data-min'));
    const max = this.parsePrice(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  onSliderInput(event) {
    this.syncFieldsFromSlider(event.currentTarget);
    this.updateApplyCount();
    this.ui?.classList.add('has-pending-change');
    this.ui?.classList.remove('is-apply-hidden');
  }

  onSliderCommit() {
    if (!this.minInput || !this.maxInput) return;
    this.setMinAndMaxValues();
    this.minInput.dispatchEvent(new Event('input', { bubbles: true }));
  }

  onApplyButtonClick() {
    this.ui?.classList.remove('has-pending-change');
    this.ui?.classList.add('is-apply-hidden');
    this.closest('details')?.removeAttribute('open');
  }

  syncFieldsFromSlider(changedRange) {
    if (!this.minRange || !this.maxRange) return;

    let min = this.parsePrice(this.minRange.value);
    let max = this.parsePrice(this.maxRange.value);

    if (min > max) {
      if (changedRange === this.minRange) {
        max = min;
        this.maxRange.value = max;
      } else {
        min = max;
        this.minRange.value = min;
      }
    }

    if (this.minInput) this.minInput.value = min <= 0 ? '' : this.formatFieldValue(min);
    if (this.maxInput) this.maxInput.value = max >= this.rangeMax ? '' : this.formatFieldValue(max);

    this.updateSliderUI(min, max);
  }

  syncSliderFromFields() {
    if (!this.minRange || !this.maxRange) return;

    const min = this.minInput?.value ? this.parsePrice(this.minInput.value) : 0;
    const max = this.maxInput?.value ? this.parsePrice(this.maxInput.value) : this.rangeMax;

    this.minRange.value = min;
    this.maxRange.value = max;
    this.updateSliderUI(min, max);
  }

  updateSliderUI(min, max) {
    if (!this.ui) return;

    const maxLimit = this.rangeMax || 1;
    const minPercent = Math.max(0, Math.min(100, (min / maxLimit) * 100));
    const maxPercent = Math.max(0, Math.min(100, (max / maxLimit) * 100));

    this.ui.style.setProperty('--price-range-start', `${minPercent}%`);
    this.ui.style.setProperty('--price-range-end', `${maxPercent}%`);

    if (this.minOutput) this.minOutput.textContent = this.formatDisplayValue(min);
    if (this.maxOutput) this.maxOutput.textContent = this.formatDisplayValue(max);

    this.ui.classList.toggle('is-active', min > 0 || max < this.rangeMax);
  }

  updateApplyCount() {
    if (!this.applyCount) return;

    const countText =
      document.getElementById('ProductCountDesktop')?.textContent ||
      document.getElementById('ProductCount')?.textContent ||
      '';
    const count = (countText.match(/\d[\d\s.,]*/) || [''])[0].replace(/\D/g, '');
    this.applyCount.textContent = this.getApplyLabel(count);
  }

  getApplyLabel(count) {
    const locale = document.documentElement.lang || window.Shopify?.locale || 'uk';
    const safeCount = count || '';

    if (locale.startsWith('pl')) return `Show ${safeCount} products`;
    if (locale.startsWith('sk')) return `Show ${safeCount} products`;
    if (locale.startsWith('en')) return `Show ${safeCount} products`;
    return `Show ${safeCount} products`;
  }

  parsePrice(value) {
    if (value == null) return 0;
    const normalized = String(value)
      .replace(/\s/g, '')
      .replace(/[^\d.,-]/g, '')
      .replace(',', '.');
    return Number.parseFloat(normalized) || 0;
  }

  formatFieldValue(value) {
    return Number(value).toFixed(2);
  }

  formatDisplayValue(value) {
    const locale = document.documentElement.lang || window.Shopify?.locale || undefined;
    const formattedValue = new Intl.NumberFormat(locale, {
      notation: 'compact',
      maximumFractionDigits: value >= 1000 ? 1 : 0,
    }).format(value);

    return `${this.currencySymbol}${formattedValue}`;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
